import { useEffect, useState } from 'react';
import { FiActivity, FiAlertCircle, FiCheckCircle, FiClock, FiDatabase, FiRefreshCw, FiServer } from 'react-icons/fi';
import api from '../services/api';
import useUser from '../hooks/useUser';

const MonitoringPage = () => {
  const { canAccessAllSites } = useUser();
  const [loading, setLoading] = useState(true);
  const [monitoringData, setMonitoringData] = useState(null);
  const [selectedSiteId, setSelectedSiteId] = useState('');

  const fetchMonitoring = async (siteIdParam = selectedSiteId) => {
    try {
      setLoading(true);
      const params = {};
      if (siteIdParam) {
        params.site_id = siteIdParam;
      }
      const res = await api.get('/system/monitoring', { params });
      setMonitoringData(res.data?.data || null);
    } catch {
      setMonitoringData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMonitoring('');
  }, []);

  const summary = monitoringData?.summary || {
    totalSites: 0,
    onlineSites: 0,
    degradedSites: 0,
    offlineSites: 0,
    exams24h: 0,
    syncedExams24h: 0,
    pendingExamSync24h: 0,
    pendingSyncJobs: 0,
    failedSyncJobs: 0,
    examCheckOkSites: 0,
    examCheckDegradedSites: 0,
    examCheckUnknownSites: 0,
    lastExamCheckAt: null,
  };

  const sites = monitoringData?.sites || [];

  const toPercent = (numerator, denominator) => {
    if (!denominator) return 0;
    return Math.round((Number(numerator || 0) / Number(denominator || 0)) * 100);
  };

  const overallSiteHealthy = summary.totalSites - summary.degradedSites - summary.offlineSites;
  const siteOnlineRate = toPercent(summary.onlineSites, summary.totalSites);
  const examSyncRate = toPercent(summary.syncedExams24h, summary.exams24h);
  const examPendingRate = toPercent(summary.pendingExamSync24h, summary.exams24h);
  const examCheckOkRate = toPercent(summary.examCheckOkSites, summary.totalSites);

  const syncHealthStatus = (() => {
    if ((summary.failedSyncJobs || 0) > 0) {
      return { label: 'Có lỗi cần xử lý', className: 'bg-rose-100 text-rose-700' };
    }
    if ((summary.pendingSyncJobs || 0) > 0 || (summary.pendingExamSync24h || 0) > 0) {
      return { label: 'Đang xử lý hàng đợi', className: 'bg-amber-100 text-amber-700' };
    }
    return { label: 'Ổn định', className: 'bg-emerald-100 text-emerald-700' };
  })();

  const connectivityBadge = (connectivity) => {
    if (connectivity === 'online') {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">Online</span>;
    }
    if (connectivity === 'degraded') {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Cảnh báo</span>;
    }
    return <span className="px-2 py-1 rounded-full text-xs font-medium bg-rose-100 text-rose-700">Offline</span>;
  };

  const examCheckBadge = (status) => {
    if (status === 'ok') {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">Ổn định</span>;
    }
    if (status === 'degraded' || status === 'error') {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Cảnh báo</span>;
    }
    return <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">Chưa có dữ liệu</span>;
  };

  const examSyncBadge = (site) => {
    if ((site.exams24h || 0) === 0) {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">Không có ca</span>;
    }
    if ((site.pendingExamSync24h || 0) === 0) {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">Ổn định</span>;
    }
    if ((site.syncedExams24h || 0) === 0) {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-rose-100 text-rose-700">Chưa sync</span>;
    }
    return <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Đang đồng bộ</span>;
  };

  const syncJobBadge = (site) => {
    const pending = Number(site.pendingSyncJobs || 0);
    const failed = Number(site.failedSyncJobs || 0);

    if (pending === 0 && failed === 0) {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">Ổn định</span>;
    }
    if (failed > 0) {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-rose-100 text-rose-700">Có lỗi</span>;
    }
    return <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Đang chờ</span>;
  };

  const formatDateTime = (value) => {
    if (!value) return '—';
    return new Date(value).toLocaleString('vi-VN');
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="text-sm text-slate-600">
          Cập nhật lúc: <span className="font-medium text-slate-800">{formatDateTime(monitoringData?.summary?.generatedAt)}</span>
        </div>

        <div className="flex items-center gap-2">
          {canAccessAllSites ? (
            <select
              value={selectedSiteId}
              onChange={(e) => {
                const nextSiteId = e.target.value;
                setSelectedSiteId(nextSiteId);
                fetchMonitoring(nextSiteId);
              }}
              className="px-3 py-2 rounded border border-slate-300 text-sm bg-white"
            >
              <option value="">Tất cả cơ sở</option>
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>
          ) : null}

          <button onClick={() => fetchMonitoring(selectedSiteId)} className="inline-flex items-center gap-2 px-3 py-2 bg-primary text-white rounded text-sm">
          <FiRefreshCw /> Refresh
        </button>
        </div>
      </div>

      {loading ? <div className="p-4 bg-white rounded border">Loading...</div> : null}

      {!loading && !monitoringData ? (
        <div className="p-4 bg-white rounded border text-sm text-slate-600">
          Không tải được dữ liệu monitoring.
        </div>
      ) : null}

      {monitoringData ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          <div className="p-4 bg-white rounded border md:col-span-2">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-slate-800 font-semibold"><FiServer /> Sức khỏe hệ thống</div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${syncHealthStatus.className}`}>{syncHealthStatus.label}</span>
            </div>
            <div className="mt-2 flex items-end justify-between gap-3">
              <div>
                <div className="text-2xl font-bold text-slate-900">{overallSiteHealthy}/{summary.totalSites}</div>
                <div className="text-xs text-slate-500">Cơ sở ổn định toàn phần</div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-emerald-600">{siteOnlineRate}%</div>
                <div className="text-xs text-slate-500">Tỷ lệ online</div>
              </div>
            </div>
            <div className="text-sm mt-2 text-slate-700">
              Online: <span className="font-semibold text-emerald-700">{summary.onlineSites}</span> · Cảnh báo: <span className="font-semibold text-amber-700">{summary.degradedSites}</span> · Offline: <span className="font-semibold text-rose-700">{summary.offlineSites}</span>
            </div>
          </div>

          <div className="p-4 bg-white rounded border">
            <div className="flex items-center gap-2 text-slate-800 font-semibold"><FiActivity /> Sync ca chụp (24h)</div>
            <div className="mt-2 flex items-end justify-between gap-3">
              <div className="text-2xl font-bold text-slate-900">{summary.syncedExams24h}/{summary.exams24h}</div>
              <div className="text-right">
                <div className="text-2xl font-bold text-emerald-600">{examSyncRate}%</div>
                <div className="text-xs text-slate-500">Đã sync</div>
              </div>
            </div>
            <div className="text-sm mt-2 text-slate-700">
              Chờ sync: <span className="font-semibold text-amber-700">{summary.pendingExamSync24h}</span> ({examPendingRate}%)
            </div>
          </div>

          <div className="p-4 bg-white rounded border">
            <div className="flex items-center gap-2 text-slate-800 font-semibold"><FiClock /> Sync jobs</div>
            <div className="mt-2 flex items-end justify-between gap-3">
              <div className="text-2xl font-bold text-amber-600">{summary.pendingSyncJobs}</div>
              <div className="text-right">
                <div className="text-2xl font-bold text-rose-600">{summary.failedSyncJobs}</div>
                <div className="text-xs text-slate-500">Lỗi 24h</div>
              </div>
            </div>
            <div className="text-sm mt-2 text-slate-700">Hàng đợi hiện tại: <span className="font-semibold text-amber-700">{summary.pendingSyncJobs}</span></div>
          </div>

          <div className="p-4 bg-white rounded border md:col-span-2 xl:col-span-2">
            <div className="flex items-center gap-2 text-slate-800 font-semibold"><FiActivity /> Auto check exam</div>
            <div className="mt-2 flex items-end justify-between gap-3">
              <div className="text-2xl font-bold text-slate-900">{summary.examCheckOkSites}/{summary.totalSites}</div>
              <div className="text-right">
                <div className="text-2xl font-bold text-emerald-600">{examCheckOkRate}%</div>
                <div className="text-xs text-slate-500">Ổn định</div>
              </div>
            </div>
            <div className="text-sm mt-2 text-slate-700">
              Ổn định: <span className="font-semibold text-emerald-700">{summary.examCheckOkSites}</span> · Cảnh báo: <span className="font-semibold text-amber-700">{summary.examCheckDegradedSites}</span> · Chưa có dữ liệu: <span className="font-semibold text-slate-700">{summary.examCheckUnknownSites}</span>
            </div>
            <div className="text-xs mt-1 text-slate-500">Lần check gần nhất: {formatDateTime(summary.lastExamCheckAt)}</div>
          </div>
        </div>
      ) : null}

      {monitoringData ? (
        <div className="bg-white rounded border overflow-hidden">
          <div className="px-4 py-3 border-b bg-slate-50 flex items-center justify-between">
            <h3 className="font-semibold text-slate-800">Chi tiết theo cơ sở</h3>
            <span className="text-xs text-slate-500">{sites.length} cơ sở</span>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Cơ sở</th>
                  <th className="text-left px-4 py-3 font-medium">Kết nối</th>
                  <th className="text-left px-4 py-3 font-medium">Sync ca chụp (24h)</th>
                  <th className="text-left px-4 py-3 font-medium">Sync jobs</th>
                  <th className="text-right px-4 py-3 font-medium">Máy trạm</th>
                  <th className="text-left px-4 py-3 font-medium">Check exam</th>
                  <th className="text-left px-4 py-3 font-medium">Lần check gần nhất</th>
                  <th className="text-left px-4 py-3 font-medium">Lần thấy gần nhất</th>
                </tr>
              </thead>
              <tbody>
                {sites.length === 0 ? (
                  <tr>
                    <td className="px-4 py-6 text-center text-slate-500" colSpan={8}>
                      Không có cơ sở trong phạm vi truy cập.
                    </td>
                  </tr>
                ) : (
                  sites.map((site, index) => (
                    <tr key={site.id} className={`gpacs-table-row border-t border-slate-100 hover:bg-slate-100 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800">{site.name}</div>
                        <div className="text-xs text-slate-500">{site.id}</div>
                      </td>
                      <td className="px-4 py-3">{connectivityBadge(site.connectivity)}</td>

                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1.5">
                          {examSyncBadge(site)}
                          <div className="text-xs text-slate-600">
                            Tổng: <span className="font-semibold text-slate-800">{site.exams24h}</span> · Đã sync: <span className="font-semibold text-emerald-700">{site.syncedExams24h}</span> · Chờ: <span className="font-semibold text-amber-700">{site.pendingExamSync24h}</span>
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1.5">
                          {syncJobBadge(site)}
                          <div className="text-xs text-slate-600">
                            Chờ: <span className="font-semibold text-amber-700">{site.pendingSyncJobs}</span> · Lỗi (24h): <span className="font-semibold text-rose-700">{site.failedSyncJobs}</span>
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-3 text-right text-slate-700">{site.activeStations}/{site.totalStations}</td>
                      <td className="px-4 py-3">{examCheckBadge(site.examCheckStatus)}</td>
                      <td className="px-4 py-3 text-slate-600">{formatDateTime(site.lastExamCheckAt)}</td>
                      <td className="px-4 py-3 text-slate-600">{formatDateTime(site.lastSeen)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <div className="p-4 bg-white rounded border">
        <div className="flex items-center gap-2 text-slate-800 font-semibold"><FiDatabase /> Phạm vi dữ liệu</div>
        <p className="text-sm text-slate-600 mt-2">
          {canAccessAllSites ? 'Bạn đang xem dữ liệu giám sát toàn hệ thống.' : 'Bạn đang xem dữ liệu giám sát trong site được phân quyền.'}
        </p>
      </div>
    </div>
  );
};

export default MonitoringPage;

